import React, { useEffect, useRef, useState } from "react";

const METRICS = [
    {
        id: "ctr",
        label: "Taux de clic (CTR)",
        sub: "Augmentation moyenne",
        value: 42,
        prefix: "+",
        suffix: "%",
    },
    {
        id: "cpc",
        label: "Coût par clic (CPC)",
        sub: "Réduction moyenne",
        value: 7,
        prefix: "−",
        suffix: "%",
    },
    {
        id: "roas",
        label: "Retour sur dépense (ROAS)",
        sub: "Amélioration globale",
        value: 38,
        prefix: "+",
        suffix: "%",
    },
    {
        id: "cpa",
        label: "Coût par acquisition (CPA)",
        sub: "Optimisation moyenne",
        value: 15,
        prefix: "−",
        suffix: "%",
    },
];

function Counter({ to, prefix, suffix, run }) {
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!run) return;
        const dur = 1600;
        const start = performance.now();
        const tick = (t) => {
            const p = Math.min(1, (t - start) / dur);
            const eased = 1 - Math.pow(1 - p, 3);
            setN(Math.round(to * eased));
            if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
    }, [run, to]);
    return (
        <span
            className="font-serif text-[#0e0e0c]"
            style={{
                fontSize: "clamp(64px, 8vw, 132px)",
                fontWeight: 300,
                lineHeight: 1,
                letterSpacing: "-0.03em",
            }}
        >
            {prefix}
            {n}
            <span className="italic-serif" style={{ fontWeight: 400 }}>
                {suffix}
            </span>
        </span>
    );
}

export default function Metrics() {
    const ref = useRef(null);
    const [run, setRun] = useState(false);

    useEffect(() => {
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => e.isIntersecting && setRun(true));
            },
            { threshold: 0.2 }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);

    return (
        <section
            id="chiffres"
            data-testid="metrics-section"
            ref={ref}
            className="relative py-28 md:py-36 border-t border-[#e8e4dd]"
        >
            <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                <div className="flex items-end justify-between mb-16">
                    <div>
                        <p className="label-eyebrow mb-5">— 01 / Performance</p>
                        <h2 className="font-serif text-5xl md:text-6xl tracking-tight text-[#0e0e0c]"
                            style={{ fontWeight: 400 }}>
                            Les chiffres,{" "}
                            <span className="italic-serif">en clair.</span>
                        </h2>
                    </div>
                    <p className="hidden md:block text-sm text-[#6b6b66] max-w-xs text-right leading-relaxed">
                        Moyennes consolidées des campagnes pilotées sur Google
                        &amp; Meta — marchés FR / EU, exercices 2024–2025.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-t border-[#e8e4dd]">
                    {METRICS.map((m) => (
                        <div
                            key={m.id}
                            data-testid={`metric-${m.id}`}
                            className="border-r border-b border-[#e8e4dd] p-8 md:p-10 bg-[#fbfaf7] hover:bg-white transition-colors duration-500 group"
                        >
                            <p className="label-eyebrow mb-10">{m.sub}</p>
                            <Counter
                                to={m.value}
                                prefix={m.prefix}
                                suffix={m.suffix}
                                run={run}
                            />
                            <div className="mt-10 pt-5 border-t border-[#e8e4dd]">
                                <p className="font-serif text-xl text-[#0e0e0c]">
                                    {m.label}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
